import { useEffect, useMemo, useState } from "react";

import { getExtensionValue, getReferenceString, parseReference } from "@medplum/core";
import {
  Attachment,
  Coverage,
  Patient,
  QuestionnaireResponse,
  Reference,
  ServiceRequest,
} from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { useDisclosure } from "@mantine/hooks";

import { SNOMEDCodes } from "@/fhir/enums/snomed";
import { Systems } from "@/fhir/enums/systems";
import { usDateFormat, usDateTimeFormat } from "@/utils/datetime";
import { getDisplayStringAsync, joinTokenString } from "@/utils/medplum";

import {
  BILLING_OPTIONS,
  BillingOptions,
  HealthGorillaLabOrderInternalStatus,
} from "../types/labs";
import { isLabResultsNoteEmpty } from "../utils/lab-results-note";
import {
  getLabOrderInternalStatus,
  getLabOrderReceivedDate,
  getUploadedImagingDetails,
} from "../utils/labs";

const EMPTY_IMAGING_DETAILS = { indication: "", conclusion: "" };

export function useLabOrderDetails(
  labOrder: ServiceRequest | undefined,
  { asClient = false }: { asClient?: boolean } = {},
) {
  const medplum = useMedplum();
  const [performer, setPerformer] = useState<string>("");
  const [requestor, setRequestor] = useState<string>("");
  const [orderedDate, setOrderedDate] = useState<string>("");
  const [receivedDate, setReceivedDate] = useState<string>("");
  const [labOrderTestList, setLabOrderTestList] = useState<string>();
  const [status, setStatus] = useState<HealthGorillaLabOrderInternalStatus>();
  const [client, setClient] = useState<string>("");
  const [clientReference, setClientReference] = useState<Reference<Patient> | undefined>();
  const [specimenCollectionTime, setSpecimenCollectionTime] = useState<string>("");
  const [resultsNote, setResultsNote] = useState<string>("");
  const [labOrderTestsResponses, setLabOrderTestsResponses] = useState<
    Record<string, QuestionnaireResponse>
  >({});
  const [labOrderTestsRequests, setLabOrderTestsRequests] = useState<
    Record<string, ServiceRequest>
  >({});
  const [coverages, setCoverages] = useState<Coverage[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  // The DiagnosticReport that holds `attachments`, so integrity alerts can name it.
  const [attachmentsOwnerReference, setAttachmentsOwnerReference] = useState<string>();
  const [imagingDetails, setImagingDetails] = useState(EMPTY_IMAGING_DETAILS);
  const [isLoading, { open: startLoading, close: stopLoading }] = useDisclosure(false);

  useEffect(() => {
    startLoading();
    // Cleared up front so a failed lookup never shows another order's clinical text.
    setImagingDetails(EMPTY_IMAGING_DETAILS);
    (async () => {
      try {
        if (!labOrder) {
          setPerformer("");
          setRequestor("");
          setClient("");
          setClientReference(undefined);
          setStatus(undefined);
          setOrderedDate("");
          setReceivedDate("");
          setLabOrderTestList("");
          setSpecimenCollectionTime("");
          setResultsNote("");
          setLabOrderTestsRequests({});
          setLabOrderTestsResponses({});
          setCoverages([]);
          setAttachments([]);
          setAttachmentsOwnerReference(undefined);
          setClient("");
          setResultsNote("");
          return;
        }

        const status = getLabOrderInternalStatus(labOrder) as HealthGorillaLabOrderInternalStatus;
        setStatus(status);

        if (asClient) {
          const orderedDate = usDateFormat(labOrder.authoredOn) || "--";
          setOrderedDate(orderedDate);

          const receivedDate = [
            HealthGorillaLabOrderInternalStatus.Reviewed,
            HealthGorillaLabOrderInternalStatus.NeedsReview,
          ].includes(status)
            ? getLabOrderReceivedDate(labOrder) || "--"
            : "--";
          setReceivedDate(receivedDate);

          const labOrderTestList =
            labOrder?.code?.coding
              ?.map((coding) => coding.display)
              .filter(Boolean)
              .join(", ") ?? "--";
          setLabOrderTestList(labOrderTestList);
        } else {
          const performer = await getDisplayStringAsync(medplum, labOrder?.performer?.[0]);
          setPerformer(performer);

          const requestor = await getDisplayStringAsync(medplum, labOrder?.requester);
          setRequestor(requestor);

          const specimenReference = labOrder?.specimen?.[0];

          if (specimenReference) {
            const specimen = await medplum.readReference(specimenReference);
            if (specimen.collection?.collectedDateTime) {
              const specimenCollectionTime = usDateTimeFormat(
                specimen.collection.collectedDateTime,
              );
              setSpecimenCollectionTime(specimenCollectionTime);
            }
          }

          const labOrderTestsRequests = (
            await Promise.all(
              labOrder?.code?.coding?.map((labOrderTest) => {
                return medplum.searchOne("ServiceRequest", {
                  "based-on": getReferenceString(labOrder),
                  code: joinTokenString(labOrderTest.system, labOrderTest.code),
                  category: joinTokenString(Systems.SNOMED, SNOMEDCodes.DiagnosticProcedure),
                  _sort: "-_lastUpdated",
                });
              }) ?? [],
            )
          ).reduce(
            (acc, request) => {
              const key = request?.code?.coding?.[0]?.code;
              if (!key) return acc;
              return { ...acc, [key]: request };
            },
            {} as Record<string, ServiceRequest>,
          );

          const labOrderTestsResponses = (
            await Promise.all(
              Object.values(labOrderTestsRequests).map(async (request) => {
                const reference = request.supportingInfo?.[0];
                if (!reference) return [undefined, undefined];

                const [resourceType, resourceId] = parseReference(reference);
                if (resourceType !== "QuestionnaireResponse") return [undefined, undefined];

                const key = request.code?.coding?.[0]?.code;
                if (!key) return [undefined, undefined];

                return [
                  key,
                  await medplum.searchOne("QuestionnaireResponse", {
                    _id: resourceId,
                  }),
                ] as [string, QuestionnaireResponse | undefined];
              }) ?? [],
            )
          ).reduce(
            (acc, [key, response]) => {
              if (!key || !response) return acc;
              return { ...acc, [key]: response };
            },
            {} as Record<string, QuestionnaireResponse>,
          );

          setLabOrderTestsRequests(labOrderTestsRequests);
          setLabOrderTestsResponses(labOrderTestsResponses);
        }

        const client = await getDisplayStringAsync(medplum, labOrder?.subject);
        setClientReference(labOrder?.subject as Reference<Patient>);
        setClient(client);

        if (labOrder.insurance) {
          const coverages = await Promise.all(
            labOrder.insurance.map((ref) => medplum.readReference(ref as Reference<Coverage>)),
          );
          setCoverages(coverages);
        } else {
          setCoverages([]);
        }

        const specimenReference = labOrder?.specimen?.[0];
        if (specimenReference) {
          const specimen = await medplum.readReference(specimenReference);
          if (specimen.collection?.collectedDateTime) {
            const specimenCollectionTime = usDateTimeFormat(specimen.collection.collectedDateTime);
            setSpecimenCollectionTime(specimenCollectionTime);
          }
        }

        const resultsNote =
          (
            await medplum.searchOne("ClinicalImpression", {
              subject: getReferenceString(labOrder?.subject),
              "supporting-info": getReferenceString(labOrder),
              _sort: "-_lastUpdated",
            })
          )?.summary ?? "";

        setResultsNote(resultsNote);

        const diagnosticReport = await medplum.searchOne("DiagnosticReport", {
          "based-on": getReferenceString(labOrder),
          _sort: "-_lastUpdated",
        });

        if (diagnosticReport?.presentedForm) {
          setAttachments(diagnosticReport.presentedForm);
          setAttachmentsOwnerReference(getReferenceString(diagnosticReport));
        } else {
          setAttachments([]);
          setAttachmentsOwnerReference(undefined);
        }
        setImagingDetails(getUploadedImagingDetails(labOrder, diagnosticReport));
      } finally {
        stopLoading();
      }
    })();
  }, [medplum, labOrder, startLoading, stopLoading, asClient]);

  const diagnoses = useMemo(() => {
    if (!labOrder?.reasonCode) return "";
    return labOrder.reasonCode
      .flatMap((reason) => {
        const coding = reason.coding?.[0];
        return coding ? [`${coding.code} - ${coding.display}`] : [];
      })
      .join(", ");
  }, [labOrder]);

  const billTo = useMemo(
    () => BILLING_OPTIONS[getExtensionValue(labOrder, "billTo") as BillingOptions] ?? "",
    [labOrder],
  );

  const orderNotes = labOrder?.note?.[0]?.text ?? "";

  const hasResultsNote = useMemo(() => !isLabResultsNoteEmpty(resultsNote), [resultsNote]);

  return {
    isLoading,
    medplum,
    orderedDate,
    receivedDate,
    performer,
    client,
    clientReference,
    requestor,
    specimenCollectionTime,
    labOrderTestList,
    labOrderTestsRequests,
    labOrderTestsResponses,
    diagnoses,
    billTo,
    status,
    orderNotes,
    resultsNote,
    setResultsNote,
    coverages,
    attachments,
    attachmentsOwnerReference,
    conclusion: imagingDetails.conclusion,
    indication: imagingDetails.indication,
    hasResultsNote,
  };
}
